/** Phase 2B — Asset upload dialog (drag-drop + form). */
import { useRef, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";

const KINDS = ["image", "video", "copy", "prompt", "document", "audio", "landing_page_ref"];

function kindFromMime(mime) {
  if (!mime) return "document";
  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("video/")) return "video";
  if (mime.startsWith("audio/")) return "audio";
  if (mime.startsWith("text/")) return "copy";
  return "document";
}

export default function AssetUploadDialog({ open, onOpenChange, onUploaded }) {
  const [file, setFile] = useState(null);
  const [name, setName] = useState("");
  const [kind, setKind] = useState("image");
  const [tags, setTags] = useState("");
  const [language, setLanguage] = useState("de");
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  const pick = (f) => {
    if (!f) return;
    setFile(f);
    if (!name) setName(f.name.replace(/\.[^.]+$/, ""));
    setKind(kindFromMime(f.type));
  };

  const reset = () => {
    setFile(null); setName(""); setKind("image"); setTags("");
    setLanguage("de"); setBusy(false); setDragOver(false);
  };

  const submit = async () => {
    if (!file) return toast.error("Pick a file first");
    if (!name.trim()) return toast.error("Name is required");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("name", name);
      fd.append("kind", kind);
      if (tags) fd.append("tags", tags);
      if (language) fd.append("language", language);
      await api.post("/assets/upload", fd,
                     { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Asset uploaded");
      reset();
      onUploaded?.();
      onOpenChange(false);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status !== 403) {
        toast.error(typeof d === "string" ? d : "Upload failed");
      }
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent data-testid="asset-upload-dialog" className="max-w-md">
        <DialogHeader><DialogTitle>Asset hochladen</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {!file ? (
            <button
              type="button"
              data-testid="asset-upload-dropzone"
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); pick(e.dataTransfer.files?.[0]); }}
              className={[
                "w-full border-2 border-dashed rounded-md p-8 text-sm text-muted-foreground text-center transition-colors",
                dragOver ? "border-primary bg-accent/30" : "hover:border-foreground",
              ].join(" ")}
            >
              Drag & drop a file here, or click to browse
              <input
                ref={fileRef}
                type="file"
                data-testid="asset-upload-file"
                className="hidden"
                onChange={(e) => pick(e.target.files?.[0])}
              />
            </button>
          ) : (
            <div className="rounded-md border p-3 text-xs flex items-center justify-between">
              <div className="truncate">
                <div className="font-medium text-sm" data-testid="asset-upload-filename">{file.name}</div>
                <div className="text-muted-foreground">{file.type || "—"} · {(file.size/1024).toFixed(1)} KB</div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => setFile(null)}>Change</Button>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Name</Label>
              <Input data-testid="asset-upload-name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
              <Label>Kind</Label>
              <Select value={kind} onValueChange={setKind}>
                <SelectTrigger data-testid="asset-upload-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {KINDS.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Language</Label>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger data-testid="asset-upload-language"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="de">DE</SelectItem>
                  <SelectItem value="en">EN</SelectItem>
                  <SelectItem value="">—</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Tags (comma separated)</Label>
              <Input data-testid="asset-upload-tags" value={tags} onChange={(e) => setTags(e.target.value)} placeholder="hero, q1, wuppertal" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <DialogClose asChild><Button variant="ghost">Abbrechen</Button></DialogClose>
          <Button onClick={submit} disabled={busy || !file} data-testid="asset-upload-submit">
            {busy ? "Wird hochgeladen…" : "Hochladen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
